import { defineStore } from 'pinia'
import apiClient, { setAuthToken } from '../api'

interface User {
  username: string
  id?: number
}

interface AuthState {
  user: User | null
  token: string | null
  loading: boolean
  error: string | null
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    // Initialize token but perform a check for 'undefined' or 'null' strings
    token: (() => {
      const storedToken = localStorage.getItem('token');
      if (storedToken === 'undefined' || storedToken === 'null') {
        localStorage.removeItem('token');
        return null;
      }
      return storedToken;
    })(),
    loading: false,
    error: null
  }),
  getters: {
    isAuthenticated: (state): boolean => !!state.token
  },
  actions: {
    setLoading(loading: boolean) {
      this.loading = loading
    },
    clearError() {
      this.error = null
    },
    clearAuthData() {
      this.user = null
      this.token = null
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      setAuthToken(null)
    },
    async login(username: string, password: string): Promise<void> {
      this.setLoading(true)
      this.clearError()
      try {
        const response = await apiClient.post('/auth/login/', { username, password })
        this.token = response.data.token
        this.user = response.data.user
        localStorage.setItem('token', this.token as string)
        localStorage.setItem('user', JSON.stringify(this.user))
        setAuthToken(this.token)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.error('Login failed:', error)
        this.error = error.response?.data?.detail || 'Login failed'
        throw error
      } finally {
        this.setLoading(false)
      }
    },
    async register(username: string, password: string): Promise<void> {
      this.setLoading(true)
      this.clearError()
      try {
        await apiClient.post('/auth/register/', { username, password })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.error('Registration failed:', error)
        this.error = error.response?.data?.detail || 'Registration failed'
        throw error
      } finally {
        this.setLoading(false)
      }
    },
    async loginAsGuest(): Promise<void> {
      this.setLoading(true)
      this.clearError()
      try {
        const response = await apiClient.post('/auth/guest-login/')
        this.token = response.data.token
        this.user = response.data.user
        localStorage.setItem('token', this.token as string)
        localStorage.setItem('user', JSON.stringify(this.user))
        setAuthToken(this.token)
        console.log('LoggedIn as Guest:', this.user)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.error('Guest login failed:', error)
        this.error = error.response?.data?.detail || 'Guest login failed'
      } finally {
        this.setLoading(false)
      }
    },
    logout(): void {
      this.clearAuthData()
    },
    async initialize(): Promise<void> {
      const token = localStorage.getItem('token')
      if (token && token !== 'undefined' && token !== 'null') {
        this.token = token
        setAuthToken(token)
        // Optionally fetch user profile here
        const user = localStorage.getItem('user');
        if (user) {
          this.user = JSON.parse(user);
        }
      } else {
        // Clear invalid token if found
        if (token) {
          this.clearAuthData()
        }
        // Auto-login as guest
        await this.loginAsGuest()
      }
    }
  }
})
